<template>
  <div id="categories-wrapper" class="package-wrapper">
    <b-card-group columns class="package-card-group mb-4">
      <b-card v-for="(_package, index) in local_record" v-bind:key="index">
        <!-- Action Icons -->
        <div class="package-actions">
          <i v-if="editings[_package.id] !== true" class="fi-pencil" @click="editPackage(_package)" />
          <span v-else>
            <i v-if="!!_package.id" class="fi-trash" @click="deletePackage(_package.id)" />
            <i class="fi-save" @click="savePackage(_package)" />
            <i class="fi-x" @click="closePackage(_package.id)" />
          </span>
        </div>

        <br>
        <!-- Display Content -->
        <div v-if="editings[_package.id] != true">
          <h4>{{ _package.name }}</h4>
          <h5>{{getVendor.local_currency}}{{ _package.rate }}</h5>
          <h6>{{ formatPackageDate(_package) }}</h6>
          <span>{{ _package.description }}</span>
          <div class="badge-wrapper mt-4">
            <b-badge v-for="tag in _package.package_tags" :key="tag.id" variant="info py-1 px-2 m-1">
              {{ tag.name }} ({{ tag.quantity }})
            </b-badge>
          </div>
        </div>

        <!-- Edit Content -->
        <div v-else>
          <info-row class="info-main-row" :sizing="2" title="Name">
            <b-col sm="12" class="input-group">
              <b-form-input
                v-model="_package.name"
                type="text"
                title="Name"
                placeholder="All inclusive"
                class="form-control"
                required
              />
            </b-col>
          </info-row>

          <Taggregator
            ref="tagsTaggregator"
            labelKey="name"
            label="select which tags to use for package categories"
            name="tags"
            :items="tags"
            :selections.sync="selected_tags[_package.id]"
            :minimum="0"
            :quickAddCallback="quickAddTag"
            :quickAddDescription="'Please provide valid tag name'"
            :quickAddMinWordCount="1"
            :quickAddMinWordLength="1"
            :quickAddHasUniqueName="true"
          >
            <template slot="result" slot-scope="{result}">
              <span class="result-label">
                {{ result.name }}
              </span>
            </template>
            <template slot="selection" slot-scope="{selection}">
              <span class="selection-label">
                {{ selection.name }}
              </span>
              <span class="selection-quantity ml-4">
                <b-form-input
                  :value="getTagQuantity(_package.id, selection.id)"
                  type="number"
                  title="Quantity"
                  placeholder="Quantity"
                  class="form-control"
                  @input="(v) => setTagQuantity(_package.id, selection.id, v)"
                />
              </span>
            </template>
          </Taggregator>

          <b-row>
            <b-col lg="12" xl="4">
              <b-form-group label="Package Rate" label-for="packageRate">
                <b-form-input id="packageRate"
                  v-model="_package.rate"
                  type="number"
                  placeholder="0.0"
                />
              </b-form-group>
            </b-col>
            <b-col lg="12" xl="8">
              <p class="sub-header mb-2">Active Dates</p>
              <div class="active-dates mb-3">
                <v-date-picker
                  class="start-date mr-2"
                  popover-direction="bottom"
                  popover-align="left"
                  mode='single'
                  show-caps
                  :value="getPackageDate(_package.starts_at)"
                  @input="(v) => setPackageDate(index, 'starts_at', v)"
                />
                -
                <v-date-picker
                  class="end-date ml-2"
                  popover-direction="bottom"
                  popover-align="right"
                  mode='single'
                  show-caps
                  :value="getPackageDate(_package.ends_at)"
                  @input="(v) => setPackageDate(index, 'ends_at', v)"
                />
              </div>
            </b-col>
          </b-row>

          <b-form-group label="Package Description" label-for="packageDescription">
            <b-form-textarea id="packageDescription"
              v-model="_package.description"
              type="text"
              placeholder="Includes all units and items..."
            />
          </b-form-group>
        </div>
      </b-card>

      <!-- Add new package -->
      <b-card
        v-if="allow_create_new"
        class="mb-2"
        title="Create New Package"
        @click="addPackage"
      >
        <i class="fi-plus cursor-pointer" />
      </b-card>

    </b-card-group>
    <div v-if="requesting" class="spinner">
      <spinner-loader />
    </div>

  </div>
</template>

<script>
  import { mapState, mapGetters
  }  from 'vuex';
  import cloneDeep     from 'lodash.clonedeep';
  import xorWith       from 'lodash/xorWith';
  import { formatBookingPeriod } from "@/utils/date-helper";

  import AlertMixin    from '@/mixins/alert-mixin';
  import InfoRow       from '@/components/InfoRow';
  import SpinnerLoader from '@/components/widgets/SpinnerLoader';
  import Taggregator   from '@/components/Taggregator';
  import CurrencyInput from  '@/components/widgets/CurrencyInput'

  export default {
    name: 'booking-packages',
    components: {
      InfoRow,
      SpinnerLoader,
      Taggregator,
      CurrencyInput
    },
    mixins: [ AlertMixin ],

    data() {
      return {
        allow_create_new: true,
        editings: {},
        local_record: [],
        selected_tags: {}
      }
    },

    async created() {
      await this.$store.dispatch('getPackages')
        .then(() => {
          this.local_record = cloneDeep(this.packages);
        })
        .catch((err) => {
          console.dir(err);
        });

      await this.$store.dispatch('fetchTags')
        .then(() => {})
        .catch((err) => {
          console.dir(err);
        });
    },

    computed: {
      ...mapState({
        requesting: state => state.packages.requesting || state.tags.isRequesting,
        packages: state => state.packages.packages,
        package_tags: state => state.packagetags.package_tags,
        tags: state => state.tags.tags,
      }),
      ...mapGetters([
        "getVendor"
      ]),
    },

    methods: {
      addPackage () {
        this.$set(this.editings, 0, true);
        this.allow_create_new = false;
        this.$set(this.selected_tags, 0, []);

        this.local_record.push({
          name: '',
          rate: 0.0,
          starts_at: '',
          ends_at: '',
          description: '',
          id: 0,
        });
      },

      editPackage (_package) {
        this.$set(this.selected_tags, _package.id, []);

        _package['package_tags'].map(tag => {
          this.selected_tags[_package.id].push({
            id: tag.tag_id,
            name: tag.name,
            quantity: tag.quantity,
            tag_id: tag.tag_id
          })
        });
        this.$set(this.editings, _package.id, true);
      },

      savePackage (_package) {
        // check package name is empty
        if (!_package.name) {
          this.showMessage('Please input the package name!', 'error');
          return;
        }

        // check tag quantity is zero or negetive
        let invalid_quantity = false;
        this.selected_tags[_package.id]
          .map(tag => {
            if (!tag.quantity || tag.quantity < 0) {
              invalid_quantity = true;
            }
          })

        if (invalid_quantity) {
          this.showMessage('The quantity of the tag is invalid!', 'error');
          return;
        }

        let params = {
          name: _package.name,
          rate: _package.rate,
          starts_at: _package.starts_at,
          ends_at: _package.ends_at,
          description: _package.description
        };

        if (!_package.id) {
          this._createPackage(params);
        } else {
          params['id'] = _package.id
          this._updatePackage(params);
        }
      },

      deletePackage (id) {
        this.showConfirm('Are you sure you want to delete your package?')
          .then(result => {
            if (result.value) {
              this._deletePackage(id);
            }
          });
      },

      closePackage (id) {
        if (id) {
          this.$set(this.editings, id, false);
        }
        this.local_record = cloneDeep(this.packages);
        this.allow_create_new = true;
      },

      formatPackageDate (_package) {
        let start_date = '~';
        let end_date = '~';
        if (_package.starts_at) {
          start_date = formatBookingPeriod(_package.starts_at)
        }
        if (_package.ends_at) {
          end_date = formatBookingPeriod(_package.ends_at)
        }
        return `${ start_date } - ${ end_date }`;
      },

      getPackageDate (value) {
        if (!value) {
          return null;
        }
        return this.$moment(value).toDate();
      },

      setPackageDate (index, field, value) {
        let date_value = this.$moment(value).format('YYYY-MM-DD');
        this.local_record[index][field] = date_value;
      },

      _createPackage (_package) {
        this.$store.dispatch('createPackage', _package)
          .then((response) => {
            this.savePackageTags(response.id, this.selected_tags[0]);
            this.successNotification('Package succesfully created!');
          })
          .catch((err) => {
            console.dir(err);
            this.errorNotification('Failed to create package');
          });
      },

      _updatePackage (_package) {
        const package_id = _package.id;
        this.$store.dispatch('updatePackage', _package)
          .then((response) => {
            this.savePackageTags(package_id, this.selected_tags[package_id], package_id);
            this.successNotification('Package succesfully updated!');
          })
          .catch((err) => {
            console.dir(err);
            this.errorNotification('Failed to update package');
          });
      },

      _deletePackage (id) {
        this.$store.dispatch('deletePackage', id)
          .then(() => {
            this.local_record = cloneDeep(this.packages);
            this.successNotification('Package succesfully deleted!');
          })
          .catch((err) => {
            console.dir(err);
            this.errorNotification('Failed to delete package');
          });
      },

      getTagQuantity (package_id, tag_id) {
        let tag_index = -1;
        let tag_quantity = 0;

        this.selected_tags[package_id]
          .filter((_tag, index) => {
            if (_tag.id === tag_id) {
              tag_index = index;
              tag_quantity = _tag.quantity;
            }
          });

        if (tag_index === -1) {
          return 0;
        }
        // if the quantity of tag is empty or zero, set it to 1 as default.
        if (!tag_quantity) {
          tag_quantity = 1;
          this.selected_tags[package_id][tag_index]['quantity'] = 1;
        }
        return tag_quantity;
      },

      setTagQuantity (package_id, tag_id, value) {
        let tag_index = -1;
        this.selected_tags[package_id]
          .filter((_tag, index) => {
            if (_tag.id === tag_id) {
              tag_index = index;
            }
          });
        if (tag_index === -1) {
          return;
        }
        this.selected_tags[package_id][tag_index]['quantity'] = value;
      },

      quickAddTag (value, items) {
        this.$store.dispatch('createTag', { name: value })
          .then((response) => {
            items.push(response);
            this.successNotification('Tag succesfully created!');
          })
          .catch((err) => {
            console.dir(err);
            this.errorNotification('Failed to create tag! :(');
          });
      },

      async savePackageTags (package_id, tags, edit_id = 0) {
        let delete_tags = [];
        let create_tags = cloneDeep(tags) || [];
        let tag_promises = [];
        let package_tags = [];

        create_tags = create_tags.map(tag => {
          tag.tag_id = 'tag_id' in tag ? tag.tag_id : tag.id;
          return tag;
        });

        // get the original tags of the package
        let _package = this.local_record.find(__package => __package.id === package_id);
        if (_package) {
          package_tags = _package['package_tags'] || [];
        }

        // get the changed tags
        let affected_tags = xorWith(package_tags, create_tags, (x, y) => {
          if (x.tag_id === y.tag_id && x.quantity === y.quantity) {
            return true;
          }
          return false;
        });

        affected_tags.map(tag => {
          // update or delete the package tags
          package_tags.filter(package_tag => {
            if (package_tag.tag_id === tag.tag_id) {
              if (package_tag.quantity === tag.quantity) {
                tag['mode'] = 'deletePackageTag';
              } else {
                tag['mode'] = 'updatePackageTag';
              }
              tag['id'] = package_tag.id;
            }
            return true;
          });

          // create the package tags
          create_tags.filter(create_tag => {
            if (create_tag.tag_id === tag.tag_id) {
              if (tag['mode'] === 'deletePackageTag') {
                tag['mode'] = '';
              } else if (tag['mode'] !== 'updatePackageTag') {
                tag['mode'] = 'createPackageTag';
              }
            }
            return true;
          });

          // add the requests for update, create or delete to promises
          if (tag['mode']) {
            let params = {
              package_id: package_id,
              tag_id: tag.tag_id,
              quantity: tag.quantity
            };
            if (tag['mode'] === 'updatePackageTag') {
              params['id'] = tag.id;
            }
            if (tag['mode'] === 'deletePackageTag') {
              params = tag.id;
            }
            tag_promises.push(this.$store.dispatch(tag['mode'], params));
          }
        });

        Promise.all(tag_promises)
          .then(() => {
            // fetch the packages again to get the updated tags
            this.$store.dispatch('getPackages')
              .then(() => {
                this.local_record = cloneDeep(this.packages);
                this.$set(this.editings, edit_id, false);
                this.allow_create_new = true;
              })
          })
          .catch((err) => {
            console.dir(err);
            this.errorNotification('Failed to save tags for the package');
          });
      }
    }
  };
</script>

<style lang="scss">
  .package-wrapper {
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;

    .info-main-row {
      margin-bottom: 10px;
    }

    .badge-wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
    }

    .package-card-group {
      margin: 12px;
      padding-top: 16px;
      column-count: 2;
    }

    .package-actions {
      position:absolute;
      top:8px;
      right:12px;
      font-size:18pt;

      i {
        cursor: pointer;
        &:hover {
          opacity: .7;
        }
      }
    }

    .spinner {
      position: absolute;
      width:100%;
      height:100%;
      left:0px;
      top:0px;
      background:rgba(255,255,255,0.7);
    }

    .cursor-pointer {
      cursor: pointer;
    }

    .selection-quantity {
      display: inline-block;
      width: 50px;
      input {
        text-align: center;
      }
    }

    .remove-selection {
      margin-top: 8px;
    }

    .active-dates {
      display: flex;
      align-items: center;
      flex-direction: row;
      justify-content: center;
      .start-date, .end-date {
        input {
          background-color: #fff;
          background-clip: padding-box;
          border: 1px solid #ced4da;
          border-radius: 0.25rem;
          color: #495057;
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.5;
          padding: 0.375rem 0.75rem;
          width: 120px;
        }
      }
    }
  }
</style>
